/**
 * HustleRoundupJoinPage — resolve short join code → room slug.
 *
 * URL: /join/:code
 * Flow: POST /api/hustleroundup/join/:code
 *   → if needs_passcode, show passcode prompt
 *   → otherwise redirect to /meet/prejoin/:slug
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { KeyRound, ArrowRight, Ban } from "lucide-react";
import { apiFetch } from "@/lib/api";

export default function HustleRoundupJoinPage() {
  const { code } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true });
  const [passcode, setPasscode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiFetch(`/hustleroundup/join/${encodeURIComponent(code)}`, {
          method: "POST",
          body: JSON.stringify({}),
        });
        if (data.needs_passcode) {
          setState({ loading: false, needsPasscode: true, room: data });
        } else {
          nav(`/meet/prejoin/${encodeURIComponent(data.slug)}`);
        }
      } catch (e) {
        setState({ loading: false, error: e.message || "Invalid code" });
      }
    })();
  }, [code, nav]);

  const submitPasscode = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr(null);
    try {
      const data = await apiFetch(`/hustleroundup/join/${encodeURIComponent(code)}`, {
        method: "POST",
        body: JSON.stringify({ passcode }),
      });
      nav(`/meet/prejoin/${encodeURIComponent(data.slug)}`);
    } catch (e) {
      setErr(e.message || "Wrong passcode");
    } finally { setBusy(false); }
  };

  if (state.loading) {
    return (
      <div data-testid="hustleroundup-join-loading"
           className="min-h-screen bg-stone-950 text-stone-400 flex items-center justify-center">
        <div className="animate-pulse">Resolving <code className="text-emerald-400 mx-1">{code}</code>…</div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div data-testid="hustleroundup-join-error"
           className="min-h-screen bg-stone-950 text-white flex items-center justify-center px-6">
        <div className="max-w-sm text-center">
          <Ban className="w-10 h-10 text-red-400 mx-auto mb-4" />
          <div className="text-xl font-bold mb-2">{state.error}</div>
          <button
            data-testid="hustleroundup-join-home"
            onClick={() => nav("/")}
            className="mt-4 px-4 py-2 rounded-full bg-emerald-500 text-stone-950 font-semibold text-sm"
          >Back home</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="hustleroundup-join"
         className="min-h-screen bg-stone-950 text-white flex items-center justify-center px-6">
      <form onSubmit={submitPasscode}
            className="w-full max-w-sm bg-stone-900/70 border border-stone-800 rounded-2xl p-8">
        <KeyRound className="w-8 h-8 text-emerald-400 mb-3" />
        <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">Join code · {code}</div>
        <h2 className="text-xl font-bold mb-1">{state.room?.name || "Meeting"}</h2>
        <p className="text-sm text-stone-400 mb-6">This room requires a passcode.</p>

        <label className="text-[11px] uppercase tracking-widest text-stone-400 block mb-1.5">Passcode</label>
        <input
          type="text"
          data-testid="hustleroundup-join-passcode"
          value={passcode} onChange={(e) => setPasscode(e.target.value)}
          className="w-full px-3 py-2.5 rounded-lg bg-stone-950 border border-stone-800 focus:border-emerald-500 outline-none text-sm"
          autoFocus
        />
        {err && <div className="text-xs text-red-400 mt-2" data-testid="hustleroundup-join-passcode-err">{err}</div>}
        <button
          type="submit"
          data-testid="hustleroundup-join-passcode-submit"
          disabled={busy || !passcode}
          className="w-full mt-5 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-stone-950 font-semibold text-sm disabled:opacity-60"
        >
          {busy ? "Verifying…" : "Continue"} <ArrowRight className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
